import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

/**
 * <DevConsolePanel /> — un botón circular flotante en la esquina inferior
 * derecha que intercepta console.log, console.warn, console.error e
 * console.info. Al hacer clic se expande en un panel redondeado (no
 * ocupa todo el ancho de la pantalla) con la mini consola completa.
 *
 * Comportamiento del botón/círculo:
 *  - Sin errores  → círculo negro mate, quieto.
 *  - Con errores  → círculo pasa a rojo con un halo pulsante, y muestra
 *    la cantidad de errores en una insignia. Warnings/info NO cambian
 *    el color, solo los errores.
 *  - Hábitos pendientes → un punto morado pulsante en la esquina
 *    inferior izquierda del botón (independiente del estado de error).
 *
 * ─────────────────────────────────────────────────────────────────────
 * CÓMO FUNCIONA POR DENTRO (resumen para quien lea el código):
 *
 * 1. Interceptación a nivel de MÓDULO (ver installDcpInterceptor).
 * 2. Batching con cola (queueRef + flush) cada 100ms.
 * 3. Deduplicación consecutiva (lastKeyRef) con contador "×N".
 * 4. Captura opt-in: los logs arrancan desactivados; hay que
 *    activarlos desde el panel. La decisión se guarda en
 *    sessionStorage para no perderla en un refresh.
 * 5. Silenciar (mute): corta en el propio interceptor.
 * 6. Aislar (isolate): filtro temporal por key.
 * 7. Filtro por nivel + búsqueda de texto.
 * 8. Contadores (`counts`) y `hasError`/`badgeValue`.
 * 9. Copiar mensaje con fallback a execCommand.
 * 10. Árbol JSON: cada entrada guarda además `rawArgs` (los argumentos
 *    originales, sin stringificar) SOLO en memoria — nunca se
 *    persisten, porque pueden tener referencias circulares, funciones,
 *    nodos DOM, etc. Si un argumento es objeto/array se renderiza con
 *    <JsonNode/>, que es colapsable y soporta referencias circulares
 *    (vía un Set "seen" que viaja en la recursión) y estructuras muy
 *    profundas/grandes (corta en profundidad 8 y en 100 claves).
 * 11. "Abrir en editor": para filas de error, se parsea el primer
 *    frame del stack (archivo:línea:columna) y se intenta abrir con
 *    el endpoint de Vite (/__open-in-editor) o, si no responde ok,
 *    el de CRA/webpack-dev-server (/__open-stack-frame-in-editor).
 *    Si ninguno existe (por ejemplo en producción), falla en
 *    silencio: no es crítico.
 * 12. Persistencia: las entradas (sin rawArgs) se guardan en
 *    sessionStorage en cada cambio, y se restauran al montar. Así un
 *    refresh o un crash duro no borra lo que ya habías logueado.
 *    idRef se sincroniza con el id máximo restaurado para no
 *    reusar ids.
 * 13. Atajos de teclado: Ctrl/Cmd + ` abre o cierra el panel desde
 *    cualquier lugar de la página; Esc (con el panel abierto) quita
 *    el aislamiento activo; / enfoca el buscador (solo si no estás
 *    escribiendo ya en otro input).
 * 14. Pin: fijar una fila la deja visible en una sección aparte
 *    ("📌 Fijados") sin importar el filtro/búsqueda/aislamiento
 *    activos en la lista principal — útil para comparar 2-3 logs
 *    puntuales sin perder de vista el resto.
 * 15. Errores globales: captura errores no controlados de JavaScript,
 *    fallos al cargar recursos y promesas rechazadas sin manejar.
 * 16. Hábitos: recordatorios configurables (movimiento, hidratación,
 *    pausa visual) pensados para sesiones largas de desarrollo frente
 *    a la pantalla. Cada hábito tiene su propio intervalo en minutos
 *    y un bloqueo (`lockMin`) que, al vencer, cubre toda la pantalla
 *    y no deja programar hasta que termine esa pausa. Van
 *    desactivados por defecto: hay que activarlos uno a uno.
 *    Se puede pausar/activar, posponer 5 min (solo si no hay bloqueo
 *    activo) o marcar como hecho. Se guardan en localStorage (persisten
 *    entre sesiones, a diferencia de los logs que van en
 *    sessionStorage). Cuando un hábito está pendiente, el botón
 *    flotante muestra un punto morado pulsante aunque el panel esté
 *    cerrado, para no depender de tener la consola abierta.
 * 17. HTTP opt-in: captura fetch y XHR (axios) como nivel `http`.
 *    Arranca desactivada; no lee el body si está apagada. Ignora
 *    hot-reload y los fetches internos de "abrir en editor".
 *
 * Uso:
 *   {process.env.NODE_ENV !== 'production' && <DevConsolePanel />}
 * ─────────────────────────────────────────────────────────────────────
 */

const LEVEL_COLOR = {
  log: '#c9d1d9',
  info: '#58a6ff',
  warn: '#d29922',
  error: '#f85149',
  http: '#3dcdc7',
};
const LEVEL_LABEL = {
  log: 'LOG',
  info: 'INFO',
  warn: 'WARN',
  error: 'ERROR',
  http: 'HTTP',
};
const MATTE_BLACK = '#0d1117';
const MATTE_BLACK_SOFT = '#161b22';
const ERROR_RED = '#f85149';
const ISOLATE_ACCENT = '#58a6ff';
const PIN_ACCENT = '#d29922';
const HABIT_ACCENT = '#a371f7';
const SESSION_STORAGE_KEY = 'dcp-entries-v1';
const NOTES_STORAGE_KEY = 'dcp-notes-v1';
const HABITS_STORAGE_KEY = 'dcp-habits-v1';
const HABITS_OPT_IN_KEY = 'dcp-habits-opt-in-v1';
const CAPTURE_STORAGE_KEY = 'dcp-capture-enabled-v1';
const HTTP_CAPTURE_STORAGE_KEY = 'dcp-http-capture-enabled-v1';
const HTTP_ACCENT = '#3dcdc7';
const LEVEL_KEY_PREFIX = /^(log|info|warn|error|http)::/;
const IGNORED_HTTP_URL = /\/__open-in-editor|\/__open-stack-frame-in-editor|hot-update|webpack-hmr|sockjs/;

// Recordatorios pensados para trabajo sedentario frente a la
// computadora. intervalMin es el punto de partida; cada uno es
// editable desde el panel.
const DEFAULT_HABITS = [
  {
    id: 'movement',
    emoji: '🚶',
    label: 'Levántate y muévete',
    intervalMin: 50,
    lockMin: 3,
    enabled: false,
  },
  {
    id: 'water',
    emoji: '💧',
    label: 'Toma agua',
    intervalMin: 60,
    lockMin: 1,
    enabled: false,
  },
  {
    id: 'eyes',
    emoji: '👀',
    label: 'Pausa visual (mira algo lejos, 20s)',
    intervalMin: 20,
    lockMin: 1,
    enabled: false,
  },
];

/**
 * Convierte cualquier argumento de console.* a texto legible (se usa
 * para el mensaje "plano" — dedup key, búsqueda, copiar, y como
 * fallback cuando no hay rawArgs disponibles, por ejemplo tras
 * restaurar desde sessionStorage).
 */
function stringifyArg(arg) {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error) return arg.stack || `${arg.name}: ${arg.message}`;
  if (
    arg === undefined ||
    typeof arg === 'bigint' ||
    typeof arg === 'function' ||
    typeof arg === 'symbol'
  ) {
    return String(arg);
  }
  try {
    const seen = new WeakSet();
    const serialized = JSON.stringify(
      arg,
      (_, value) => {
        if (typeof value === 'bigint') return `${value}n`;
        if (value instanceof Error) {
          return {
            name: value.name,
            message: value.message,
            stack: value.stack,
          };
        }
        if (value && typeof value === 'object') {
          if (seen.has(value)) return '[Circular]';
          seen.add(value);
        }
        return value;
      },
      2
    );
    return serialized ?? String(arg);
  } catch {
    return String(arg);
  }
}

function fallbackCopyText(text) {
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  textarea.setAttribute('readonly', '');
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

// ── Árbol JSON colapsable ────────────────────────────────────────────
function isExpandable(value) {
  return value !== null && typeof value === 'object';
}

function formatPrimitive(value) {
  if (typeof value === 'string') return `"${value}"`;
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'bigint') return `${value}n`;
  return String(value);
}

function primitiveColor(value) {
  if (typeof value === 'string') return '#a5d6ff';
  if (typeof value === 'number' || typeof value === 'bigint') return '#79c0ff';
  if (typeof value === 'boolean') return '#ff7b72';
  if (value === null || value === undefined) return '#8b949e';
  return '#c9d1d9';
}

// depth y seen viajan por la recursión: depth limita cuán profundo se
// renderiza (protección ante estructuras enormes), seen detecta
// referencias circulares sin volver a stringificar todo el objeto.
function JsonNode({ label, value, depth, seen }) {
  const [collapsed, setCollapsed] = useState(depth > 0);

  if (!isExpandable(value)) {
    return (
      <div style={{ paddingLeft: depth * 14 }}>
        {label != null && <span style={{ opacity: 0.6 }}>{label}: </span>}
        <span style={{ color: primitiveColor(value) }}>
          {formatPrimitive(value)}
        </span>
      </div>
    );
  }
  if (seen.has(value)) {
    return (
      <div style={{ paddingLeft: depth * 14 }}>
        {label != null && <span style={{ opacity: 0.6 }}>{label}: </span>}
        <span style={{ color: '#d29922' }}>[Circular]</span>
      </div>
    );
  }
  if (depth > 8) {
    return (
      <div style={{ paddingLeft: depth * 14 }}>
        {label != null && <span style={{ opacity: 0.6 }}>{label}: </span>}
        <span style={{ opacity: 0.5 }}>…</span>
      </div>
    );
  }

  const nextSeen = new Set(seen);
  nextSeen.add(value);
  const isArray = Array.isArray(value);
  const entries = isArray ? value.map((v, i) => [i, v]) : Object.entries(value);
  const shown = entries.slice(0, 100);
  const extra = entries.length - shown.length;
  const preview = isArray
    ? `Array(${value.length})`
    : value instanceof Error
    ? `${value.name}: ${value.message}`
    : `{${entries.length}}`;

  return (
    <div style={{ paddingLeft: depth * 14 }}>
      <span
        onClick={() => setCollapsed((c) => !c)}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <span style={{ opacity: 0.5, display: 'inline-block', width: 12 }}>
          {collapsed ? '▶' : '▼'}
        </span>
        {label != null && <span style={{ opacity: 0.6 }}>{label}: </span>}
        <span style={{ color: '#8b949e' }}>{preview}</span>
      </span>
      {!collapsed && (
        <div>
          {shown.map(([k, v]) => (
            <JsonNode
              key={k}
              label={k}
              value={v}
              depth={depth + 1}
              seen={nextSeen}
            />
          ))}
          {extra > 0 && (
            <div style={{ paddingLeft: (depth + 1) * 14, opacity: 0.5 }}>
              +{extra} más…
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── "Abrir en editor" desde el stack trace ──────────────────────────
function parseFirstFrame(stackText) {
  if (!stackText) return null;
  const lines = stackText.split('\n');
  for (const line of lines) {
    const match = line.match(/(https?:\/\/[^\s)]+|\/[^\s)]+):(\d+):(\d+)/);
    if (match) {
      let file = match[1];
      // Si es una URL absoluta (http://localhost:xxxx/src/App.jsx) nos
      // quedamos solo con el path, que es lo que esperan ambos endpoints.
      try {
        file = new URL(file).pathname;
      } catch {
        // ya era un path relativo/absoluto de filesystem, se deja igual
      }
      return { file, line: Number(match[2]), column: Number(match[3]) };
    }
  }
  return null;
}

async function openInEditor(frame) {
  const { file, line, column } = frame;
  // Se prueba primero el endpoint de Vite; si no da ok, el de
  // CRA/webpack-dev-server. Si ninguno existe (build de producción,
  // otro bundler), falla en silencio: no es crítico.
  try {
    const viteRes = await fetch(
      `/__open-in-editor?file=${encodeURIComponent(
        `${file}:${line}:${column}`
      )}`
    );
    if (viteRes.ok) return;
  } catch {
    // sigue al siguiente intento
  }
  try {
    await fetch(
      `/__open-stack-frame-in-editor?fileName=${encodeURIComponent(
        file
      )}&lineNumber=${line}&colNumber=${column}`
    );
  } catch {
    // no hay dev server compatible corriendo
  }
}

function actionButtonStyle(color) {
  return {
    background: 'transparent',
    border: '1px solid #30363d',
    color,
    borderRadius: 4,
    padding: '0 4px',
    cursor: 'pointer',
    fontSize: 10,
  };
}

function formatLockClock(ms) {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function HabitLockOverlay({ habits, now, onDone }) {
  const overlayRef = useRef(null);
  const remainingMs = Math.max(
    0,
    ...habits.map((h) =>
      h.lockUntil ? h.lockUntil - now : (h.lockMin || 1) * 60000
    )
  );
  const allUnlocked = remainingMs <= 0 && habits.every((h) => h.lockUntil);

  useEffect(() => {
    overlayRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dcp-habit-lock-title"
      tabIndex={-1}
      onKeyDown={(e) => {
        if (e.key === 'Tab') e.preventDefault();
        if (e.key === '`' && (e.ctrlKey || e.metaKey)) e.preventDefault();
        if (e.key === 'Escape') e.preventDefault();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000000,
        background: MATTE_BLACK,
        color: '#c9d1d9',
        fontFamily: "'SF Mono', Consolas, Menlo, monospace",
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        style={{
          width: 'min(480px, 100%)',
          border: `1px solid ${HABIT_ACCENT}`,
          borderRadius: 16,
          background: MATTE_BLACK_SOFT,
          boxShadow: '0 0 0 4px rgba(163,113,247,0.12), 0 16px 48px rgba(0,0,0,0.6)',
          padding: '28px 24px 22px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
          textAlign: 'center',
        }}
      >
        <div style={{ fontSize: 36, lineHeight: 1 }}>
          {habits.map((h) => h.emoji).join(' ')}
        </div>
        <div>
          <div
            id="dcp-habit-lock-title"
            style={{ fontSize: 18, fontWeight: 700, color: HABIT_ACCENT }}
          >
            {allUnlocked ? 'Pausa lista' : 'Pausa obligatoria'}
          </div>
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 6 }}>
            {allUnlocked
              ? 'Marca cada hábito como hecho para volver a programar.'
              : 'La consola bloquea la pantalla. No puedes programar hasta terminar.'}
          </div>
        </div>

        {!allUnlocked && (
          <div
            style={{
              fontSize: 48,
              fontWeight: 700,
              letterSpacing: 2,
              color: HABIT_ACCENT,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {formatLockClock(remainingMs)}
          </div>
        )}

        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          {habits.map((h) => {
            const locked = !h.lockUntil || h.lockUntil > now;
            return (
              <div
                key={h.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  border: `1px solid ${HABIT_ACCENT}`,
                  borderRadius: 8,
                  background: 'rgba(163,113,247,0.08)',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: 22, flexShrink: 0 }}>{h.emoji}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{h.label}</div>
                  <div style={{ fontSize: 11, opacity: 0.65 }}>
                    {locked
                      ? `quedan ${formatLockClock(
                          h.lockUntil
                            ? h.lockUntil - now
                            : (h.lockMin || 1) * 60000
                        )}`
                      : 'listo para marcar'}
                  </div>
                </span>
                <button
                  type="button"
                  disabled={locked}
                  onClick={() => onDone(h.id)}
                  style={{
                    ...actionButtonStyle(locked ? '#8b949e' : '#3fb950'),
                    padding: '6px 10px',
                    fontSize: 12,
                    cursor: locked ? 'not-allowed' : 'pointer',
                    opacity: locked ? 0.45 : 1,
                    flexShrink: 0,
                  }}
                >
                  hecho
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Botón circular con ícono, usado en acciones puntuales (agregar,
// eliminar, pausar/activar) donde un símbolo es más rápido de leer
// que una palabra.
function iconButtonStyle(color, size = 22, disabled = false) {
  return {
    width: size,
    height: size,
    flexShrink: 0,
    borderRadius: '50%',
    border: `1px solid ${disabled ? '#30363d' : color}`,
    background: 'transparent',
    color: disabled ? '#8b949e' : color,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.5 : 1,
  };
}

function PlusIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
      <rect x="5" y="4" width="5" height="16" rx="1" />
      <rect x="14" y="4" width="5" height="16" rx="1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="6,3 20,12 6,21" />
    </svg>
  );
}

// ── Persistencia en sessionStorage ──────────────────────────────────
function loadPersistedEntries() {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function loadCaptureEnabled() {
  try {
    return sessionStorage.getItem(CAPTURE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function loadHttpCaptureEnabled() {
  try {
    return sessionStorage.getItem(HTTP_CAPTURE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function loadPersistedNotes() {
  try {
    const raw = localStorage.getItem(NOTES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

// ── Persistencia de hábitos en localStorage ─────────────────────────
// A diferencia de los logs (sessionStorage, se borran al cerrar la
// pestaña), los hábitos viven en localStorage porque el objetivo es
// que el hábito persista día a día, no solo dentro de una sesión.
function loadPersistedHabits() {
  const now = Date.now();
  const fresh = () =>
    DEFAULT_HABITS.map((h) => ({
      ...h,
      enabled: false,
      nextDueAt: now + h.intervalMin * 60000,
    }));
  try {
    const raw = localStorage.getItem(HABITS_STORAGE_KEY);
    if (!raw) return fresh();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return fresh();
    // Combina lo guardado con la lista de hábitos por defecto, para
    // no perder el progreso si en una actualización futura se agregan
    // o quitan hábitos nuevos.
    const byId = new Map(parsed.map((h) => [h.id, h]));
    // Los hábitos viejos arrancaban activos. Una sola vez se apagan
    // para que queden opt-in; después se respeta lo que el usuario
    // active o pause.
    let alreadyOptIn = false;
    try {
      alreadyOptIn = localStorage.getItem(HABITS_OPT_IN_KEY) === '1';
      if (!alreadyOptIn) localStorage.setItem(HABITS_OPT_IN_KEY, '1');
    } catch {
      // no-op
    }
    return DEFAULT_HABITS.map((def) => {
      const saved = byId.get(def.id);
      if (!saved) {
        return {
          ...def,
          enabled: false,
          nextDueAt: now + def.intervalMin * 60000,
        };
      }
      return {
        ...def,
        ...saved,
        lockMin: saved.lockMin ?? def.lockMin,
        enabled: alreadyOptIn ? Boolean(saved.enabled) : false,
        lockUntil: alreadyOptIn ? saved.lockUntil : null,
      };
    });
  } catch {
    return fresh();
  }
}

// ── Interceptor de console.* a nivel de MÓDULO ──────────────────────
const dcpListeners = new Set();
let dcpOriginalConsole = null;

function installDcpInterceptor() {
  if (dcpOriginalConsole) return;
  dcpOriginalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
  };
  ['log', 'info', 'warn', 'error'].forEach((level) => {
    console[level] = (...args) => {
      dcpOriginalConsole[level](...args);
      dcpListeners.forEach((listener) => listener(level, args));
    };
  });
}

function uninstallDcpInterceptor() {
  if (!dcpOriginalConsole) return;
  console.log = dcpOriginalConsole.log;
  console.info = dcpOriginalConsole.info;
  console.warn = dcpOriginalConsole.warn;
  console.error = dcpOriginalConsole.error;
  dcpOriginalConsole = null;
}

// ── Interceptor de fetch + XHR (axios) ──────────────────────────────
const dcpHttpListeners = new Set();
let dcpHttpCaptureOn = false;
let dcpHttpPatched = false;
let dcpOriginalFetch = null;
let dcpOriginalXhrOpen = null;
let dcpOriginalXhrSend = null;

function isIgnoredHttpUrl(url) {
  return !url || IGNORED_HTTP_URL.test(String(url));
}

function displayHttpUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    parsed.searchParams.delete('data');
    parsed.searchParams.delete('imestamp');
    const path = `${parsed.pathname}${parsed.search}`;
    return parsed.origin === window.location.origin
      ? path || '/'
      : `${parsed.origin}${path}`;
  } catch {
    return String(url).replace(/[?&](data|imestamp)=[^&]*/g, '');
  }
}

function summarizeHttpBody(body) {
  if (body == null) return undefined;
  if (typeof body === 'string') {
    return body.length > 2000 ? `${body.slice(0, 2000)}…` : body;
  }
  if (typeof FormData !== 'undefined' && body instanceof FormData) {
    return { type: 'FormData', keys: Array.from(body.keys()) };
  }
  if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) {
    return body.toString();
  }
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    return { type: 'Blob', size: body.size };
  }
  if (typeof ArrayBuffer !== 'undefined' && body instanceof ArrayBuffer) {
    return { type: 'ArrayBuffer', byteLength: body.byteLength };
  }
  try {
    return JSON.parse(JSON.stringify(body));
  } catch {
    return String(body);
  }
}

function parseHttpPreview(text, contentType) {
  if (text == null || text === '') return undefined;
  const preview = text.length > 4000 ? `${text.slice(0, 4000)}…` : text;
  if (contentType && contentType.includes('json')) {
    try {
      return JSON.parse(text.length > 4000 ? text.slice(0, 4000) : text);
    } catch {
      return preview;
    }
  }
  return preview;
}

function emitHttpLog(entry) {
  dcpHttpListeners.forEach((listener) => listener(entry));
}

function installDcpHttpInterceptor() {
  if (dcpHttpPatched || typeof window === 'undefined') return;
  dcpHttpPatched = true;

  if (typeof window.fetch === 'function') {
    dcpOriginalFetch = window.fetch.bind(window);
    window.fetch = async function dcpPatchedFetch(input, init) {
      const method = (
        init?.method ||
        (input && typeof input === 'object' && 'method' in input
          ? input.method
          : 'GET') ||
        'GET'
      ).toUpperCase();
      const url =
        typeof input === 'string'
          ? input
          : input instanceof URL
            ? input.href
            : input?.url ?? String(input);

      if (!dcpHttpCaptureOn || isIgnoredHttpUrl(url)) {
        return dcpOriginalFetch(input, init);
      }

      const started = performance.now();
      try {
        const res = await dcpOriginalFetch(input, init);
        let response;
        try {
          const contentType = res.headers.get('content-type') || '';
          if (
            contentType.includes('json') ||
            contentType.includes('text') ||
            contentType.includes('xml')
          ) {
            response = parseHttpPreview(await res.clone().text(), contentType);
          } else {
            response = { type: contentType || 'binary', skipped: true };
          }
        } catch {
          // el body no se pudo leer; la request en sí ya resolvió
        }
        emitHttpLog({
          method,
          url,
          status: res.status,
          ok: res.ok,
          duration: Math.round(performance.now() - started),
          request: summarizeHttpBody(init?.body),
          response,
        });
        return res;
      } catch (err) {
        emitHttpLog({
          method,
          url,
          status: 0,
          ok: false,
          duration: Math.round(performance.now() - started),
          request: summarizeHttpBody(init?.body),
          error: err?.message || String(err),
        });
        throw err;
      }
    };
  }

  const Xhr = window.XMLHttpRequest;
  if (!Xhr?.prototype) return;

  dcpOriginalXhrOpen = Xhr.prototype.open;
  dcpOriginalXhrSend = Xhr.prototype.send;

  Xhr.prototype.open = function dcpXhrOpen(method, url, ...rest) {
    this.__dcp = {
      method: String(method || 'GET').toUpperCase(),
      url: String(url || ''),
    };
    return dcpOriginalXhrOpen.call(this, method, url, ...rest);
  };

  Xhr.prototype.send = function dcpXhrSend(body) {
    const meta = this.__dcp;
    if (!dcpHttpCaptureOn || !meta || isIgnoredHttpUrl(meta.url)) {
      return dcpOriginalXhrSend.call(this, body);
    }

    const started = performance.now();
    const finish = () => {
      this.removeEventListener('loadend', finish);
      let response;
      try {
        const responseType = this.responseType;
        if (!responseType || responseType === 'text' || responseType === 'json') {
          const contentType = this.getResponseHeader?.('content-type') || '';
          response =
            responseType === 'json' && this.response != null
              ? this.response
              : parseHttpPreview(this.responseText, contentType);
        }
      } catch {
        // ignore
      }
      emitHttpLog({
        method: meta.method,
        url: meta.url,
        status: this.status || 0,
        ok: this.status >= 200 && this.status < 400,
        duration: Math.round(performance.now() - started),
        request: summarizeHttpBody(body),
        response,
        error:
          this.status === 0 ? 'Red o solicitud bloqueada' : undefined,
      });
    };
    this.addEventListener('loadend', finish);
    return dcpOriginalXhrSend.call(this, body);
  };
}

function uninstallDcpHttpInterceptor() {
  if (!dcpHttpPatched) return;
  if (dcpOriginalFetch) window.fetch = dcpOriginalFetch;
  if (dcpOriginalXhrOpen) {
    window.XMLHttpRequest.prototype.open = dcpOriginalXhrOpen;
  }
  if (dcpOriginalXhrSend) {
    window.XMLHttpRequest.prototype.send = dcpOriginalXhrSend;
  }
  dcpOriginalFetch = null;
  dcpOriginalXhrOpen = null;
  dcpOriginalXhrSend = null;
  dcpHttpPatched = false;
}

// ── Fila de log (se usa tanto en la lista principal como en Fijados) ──
function LogRow({
  entry: e,
  isolateKey,
  copiedId,
  pinnedIds,
  onCopy,
  onToggleIsolate,
  onMute,
  onTogglePin,
}) {
  const isPinned = pinnedIds.has(e.id);
  const frame = e.level === 'error' ? parseFirstFrame(e.message) : null;

  return (
    <div
      className="dcp-row"
      style={{
        display: 'flex',
        gap: 8,
        paddingTop: 2,
        paddingBottom: 2,
        paddingRight: 10,
        // paddingLeft explícito siempre (no shorthand) para poder
        // sobreescribirlo sin mezclar formas cortas y largas — evita el
        // warning de React sobre estilos conflictivos.
        paddingLeft: isolateKey === e.key ? 7 : 10,
        borderBottom: '1px solid #161b22',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        alignItems: 'flex-start',
        ...(isolateKey === e.key && {
          background: 'rgba(88,166,255,0.06)',
          borderLeft: `3px solid ${ISOLATE_ACCENT}`,
        }),
        ...(isPinned &&
          isolateKey !== e.key && {
            background: 'rgba(210,153,34,0.06)',
          }),
      }}
    >
      <span style={{ opacity: 0.4, flexShrink: 0 }}>{e.time}</span>
      <span
        style={{ color: LEVEL_COLOR[e.level], flexShrink: 0, fontWeight: 700 }}
      >
        {LEVEL_LABEL[e.level]}
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        {e.rawArgs
          ? e.rawArgs.map((arg, i) =>
              isExpandable(arg) ? (
                <div key={i}>
                  <JsonNode value={arg} depth={0} seen={new Set()} />
                </div>
              ) : (
                <span key={i}>{stringifyArg(arg)} </span>
              )
            )
          : e.message}
      </span>
      {e.count > 1 && (
        <span
          style={{
            flexShrink: 0,
            background: isolateKey === e.key ? ISOLATE_ACCENT : '#30363d',
            color: isolateKey === e.key ? '#0d1117' : '#c9d1d9',
            borderRadius: 10,
            padding: '0 7px',
            fontSize: 10,
            fontWeight: 700,
            lineHeight: '16px',
            height: 16,
          }}
        >
          ×{e.count}
        </span>
      )}
      <span
        className="dcp-actions"
        style={{
          flexShrink: 0,
          display: 'flex',
          gap: 4,
          ...(isolateKey === e.key && { opacity: 1 }),
        }}
      >
        {frame && (
          <button
            type="button"
            onClick={() => openInEditor(frame)}
            title={`Abrir ${frame.file}:${frame.line} en el editor`}
            style={actionButtonStyle('#3fb950')}
          >
            editor
          </button>
        )}
        <button
          type="button"
          onClick={() => onCopy(e)}
          title="Copiar este mensaje"
          style={{
            ...actionButtonStyle(copiedId === e.id ? '#3fb950' : '#c9d1d9'),
            minWidth: 46,
          }}
        >
          {copiedId === e.id ? '✓ copiado' : 'copiar'}
        </button>
        <button
          type="button"
          onClick={() => onToggleIsolate(e.key)}
          title="Mostrar solo este mensaje"
          style={actionButtonStyle('#58a6ff')}
        >
          aislar
        </button>
        <button
          type="button"
          onClick={() => onTogglePin(e.id)}
          title={isPinned ? 'Quitar de fijados' : 'Fijar este mensaje'}
          style={actionButtonStyle(isPinned ? PIN_ACCENT : '#8b949e')}
        >
          {isPinned ? '📌 quitar' : '📌 fijar'}
        </button>
        <button
          type="button"
          onClick={() => onMute(e.key)}
          title="Silenciar este mensaje"
          style={actionButtonStyle('#f85149')}
        >
          silenciar
        </button>
      </span>
    </div>
  );
}

export default function DevConsolePanel() {
  const [entries, setEntries] = useState(loadPersistedEntries);
  const [notes, setNotes] = useState(loadPersistedNotes);
  const [notesOpen, setNotesOpen] = useState(false);
  const [noteLocation, setNoteLocation] = useState('');
  const [noteComment, setNoteComment] = useState('');
  const [habits, setHabits] = useState(loadPersistedHabits);
  const [habitsOpen, setHabitsOpen] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [open, setOpen] = useState(false);
  const [captureEnabled, setCaptureEnabled] = useState(loadCaptureEnabled);
  const [httpCaptureEnabled, setHttpCaptureEnabled] = useState(
    loadHttpCaptureEnabled
  );
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [mutedKeys, setMutedKeys] = useState(() => new Set());
  const [isolateKey, setIsolateKey] = useState(null);
  const [pinnedIds, setPinnedIds] = useState(() => new Set());
  const [copiedId, setCopiedId] = useState(null);
  const copyTimeoutRef = useRef(null);
  const idRef = useRef(0);
  const bottomRef = useRef(null);
  const searchInputRef = useRef(null);
  const queueRef = useRef([]);
  const flushScheduledRef = useRef(false);
  const flushTimeoutRef = useRef(null);
  const lastKeyRef = useRef(null);
  const mutedKeysRef = useRef(mutedKeys);
  const captureEnabledRef = useRef(captureEnabled);
  const httpCaptureEnabledRef = useRef(httpCaptureEnabled);
  // Espejos para que el listener de teclado (montado una sola vez)
  // siempre lea el valor más reciente sin tener que recrearse.
  const openRef = useRef(open);
  const isolateKeyRef = useRef(isolateKey);
  const habitLockRef = useRef(false);

  useEffect(() => {
    mutedKeysRef.current = mutedKeys;
  }, [mutedKeys]);
  useEffect(() => {
    captureEnabledRef.current = captureEnabled;
    try {
      sessionStorage.setItem(CAPTURE_STORAGE_KEY, captureEnabled ? '1' : '0');
    } catch {
      // no-op
    }
  }, [captureEnabled]);
  useEffect(() => {
    httpCaptureEnabledRef.current = httpCaptureEnabled;
    dcpHttpCaptureOn = httpCaptureEnabled;
    try {
      sessionStorage.setItem(
        HTTP_CAPTURE_STORAGE_KEY,
        httpCaptureEnabled ? '1' : '0'
      );
    } catch {
      // no-op
    }
  }, [httpCaptureEnabled]);
  useEffect(() => {
    openRef.current = open;
  }, [open]);
  useEffect(() => {
    isolateKeyRef.current = isolateKey;
  }, [isolateKey]);

  // Sincroniza idRef con el id más alto restaurado desde
  // sessionStorage, para no reusar un id ya usado antes del reload.
  // Se lee `prev` en vez de `entries` para no depender de un valor
  // externo y no necesitar deshabilitar ninguna regla de lint.
  useEffect(() => {
    setEntries((prev) => {
      const maxId = prev.reduce((max, e) => Math.max(max, e.id), 0);
      if (maxId > idRef.current) idRef.current = maxId;
      return prev; // no se cambia nada, solo se lee
    });
  }, []);

  // Persiste las entradas (sin rawArgs, que puede tener referencias
  // circulares, funciones o nodos DOM no serializables) en cada
  // cambio. sessionStorage puede fallar en modo privado o por cuota
  // excedida: no es crítico, simplemente no persiste esa vez.
  useEffect(() => {
    try {
      const serializable = entries.map(({ rawArgs, ...rest }) => rest);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(serializable));
    } catch {
      // no-op
    }
  }, [entries]);

  useEffect(() => {
    try {
      localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(notes));
    } catch {
      // no-op
    }
  }, [notes]);

  // Persiste hábitos (intervalos, estado activo/pausado y próxima
  // fecha de vencimiento) en cada cambio.
  useEffect(() => {
    try {
      localStorage.setItem(HABITS_STORAGE_KEY, JSON.stringify(habits));
    } catch {
      // no-op
    }
  }, [habits]);

  const hasDueHabit = habits.some((h) => h.enabled && now >= h.nextDueAt);
  habitLockRef.current = hasDueHabit;

  // "Reloj" para recalcular qué hábitos están vencidos y cuánto falta
  // para los que no. 15s basta en espera; durante un bloqueo se
  // actualiza cada segundo para el countdown MM:SS.
  useEffect(() => {
    habitLockRef.current = hasDueHabit;
    const intervalMs = hasDueHabit ? 1000 : 15000;
    const tick = setInterval(() => setNow(Date.now()), intervalMs);
    const onVisible = () => {
      if (document.visibilityState === 'visible') setNow(Date.now());
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [hasDueHabit]);

  // Al vencer un hábito, arranca su bloqueo de pantalla (lockUntil).
  // Se persiste para que un refresh no salte la pausa.
  useEffect(() => {
    if (!hasDueHabit) return;
    const t = Date.now();
    setHabits((prev) => {
      let changed = false;
      const next = prev.map((h) => {
        if (!h.enabled || t < h.nextDueAt || h.lockUntil) return h;
        changed = true;
        const lockMin = Math.min(30, Math.max(1, Math.round(h.lockMin) || 1));
        return { ...h, lockUntil: t + lockMin * 60000 };
      });
      return changed ? next : prev;
    });
  }, [hasDueHabit, now]);

  useEffect(() => {
    installDcpInterceptor();
    installDcpHttpInterceptor();

    function flush() {
      flushScheduledRef.current = false;
      flushTimeoutRef.current = null;
      if (queueRef.current.length === 0) return;
      const ops = queueRef.current;
      queueRef.current = [];
      setEntries((prev) => {
        const next = [...prev];
        const indexById = new Map(
          next.map((entry, index) => [entry.id, index])
        );

        for (const op of ops) {
          if (op.type === 'add') {
            indexById.set(op.entry.id, next.length);
            next.push(op.entry);
          } else if (op.type === 'inc') {
            const index = indexById.get(op.id);
            if (index !== undefined) {
              const entry = next[index];
              next.splice(index, 1);
              next.push({ ...entry, count: entry.count + 1, time: op.time });
              indexById.forEach((idx, id) => {
                if (idx > index) indexById.set(id, idx - 1);
              });
              indexById.set(op.id, next.length - 1);
            }
          }
        }
        return next.slice(-300);
      });
    }

    const enqueueEntry = (level, args, keyMessage) => {
      const parts = args.map(stringifyArg);
      const message = parts.join(' ');
      const key = `${level}::${keyMessage ?? message}`;
      if (mutedKeysRef.current.has(key)) return;

      const time = new Date().toLocaleTimeString();

      if (lastKeyRef.current && lastKeyRef.current.key === key) {
        queueRef.current.push({
          type: 'inc',
          id: lastKeyRef.current.id,
          key,
          time,
        });
      } else {
        idRef.current += 1;
        const id = idRef.current;
        lastKeyRef.current = { key, id };
        // rawArgs vive solo en memoria (nunca se persiste) y habilita
        // el árbol JSON colapsable para objetos/arrays.
        queueRef.current.push({
          type: 'add',
          entry: {
            id,
            level,
            key,
            message,
            time,
            parts,
            count: 1,
            rawArgs: args,
          },
        });
      }
      if (!flushScheduledRef.current) {
        flushScheduledRef.current = true;
        flushTimeoutRef.current = setTimeout(flush, 100);
      }
    };

    const handleLog = (level, args) => {
      if (!captureEnabledRef.current) return;
      enqueueEntry(level, args);
    };

    const handleHttp = (payload) => {
      if (!httpCaptureEnabledRef.current) return;
      const url = displayHttpUrl(payload.url);
      const status = payload.status || 'ERR';
      const line = `${payload.method} ${url} → ${status} (${payload.duration}ms)`;
      enqueueEntry(
        'http',
        [
          line,
          {
            method: payload.method,
            url: payload.url,
            status: payload.status,
            ok: payload.ok,
            durationMs: payload.duration,
            request: payload.request,
            response: payload.response,
            error: payload.error,
          },
        ],
        `${payload.method} ${url} → ${status}`
      );
    };

    const handleGlobalError = (event) => {
      if (event instanceof ErrorEvent) {
        const fallback = `${event.message} (${event.filename}:${event.lineno}:${event.colno})`;
        handleLog('error', ['Error no controlado:', event.error ?? fallback]);
        return;
      }

      const resource = event.target;
      const resourceUrl = resource?.src ?? resource?.href;
      if (resourceUrl) {
        handleLog('error', [`Error al cargar recurso: ${resourceUrl}`]);
      }
    };

    const handleUnhandledRejection = (event) => {
      handleLog('error', [
        'Promesa rechazada sin manejar:',
        event.reason ?? 'Motivo desconocido',
      ]);
    };

    dcpListeners.add(handleLog);
    dcpHttpListeners.add(handleHttp);
    window.addEventListener('error', handleGlobalError, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current);
      flushTimeoutRef.current = null;
      flushScheduledRef.current = false;
      queueRef.current = [];
      dcpListeners.delete(handleLog);
      dcpHttpListeners.delete(handleHttp);
      window.removeEventListener('error', handleGlobalError, true);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
      if (dcpListeners.size === 0) uninstallDcpInterceptor();
      if (dcpHttpListeners.size === 0) uninstallDcpHttpInterceptor();
    };
  }, []);

  // Atajos de teclado: Ctrl/Cmd + ` abre o cierra el panel desde
  // cualquier parte de la página; Esc quita el aislamiento (solo con
  // el panel abierto); / enfoca el buscador (si no se está ya
  // escribiendo en otro input, para no robarle la tecla a la página).
  useEffect(() => {
    function handleKeyDown(e) {
      const target = e.target;
      const isTyping =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);

      if (e.key === '`' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        if (habitLockRef.current) return;
        setOpen((o) => !o);
        return;
      }

      if (!openRef.current) return;

      if (e.key === 'Escape' && isolateKeyRef.current) {
        setIsolateKey(null);
        return;
      }

      if (e.key === '/' && !isTyping) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [entries, open]);

  const clear = useCallback(() => {
    if (flushTimeoutRef.current) clearTimeout(flushTimeoutRef.current);
    flushTimeoutRef.current = null;
    flushScheduledRef.current = false;
    queueRef.current = [];
    setEntries([]);
    lastKeyRef.current = null;
    setPinnedIds(new Set());
  }, []);

  const toggleCapture = useCallback(() => {
    setCaptureEnabled((enabled) => {
      const next = !enabled;
      captureEnabledRef.current = next;
      lastKeyRef.current = null;
      return next;
    });
  }, []);

  const toggleHttpCapture = useCallback(() => {
    setHttpCaptureEnabled((enabled) => {
      const next = !enabled;
      httpCaptureEnabledRef.current = next;
      dcpHttpCaptureOn = next;
      lastKeyRef.current = null;
      return next;
    });
  }, []);

  const muteKey = useCallback((key) => {
    const nextMutedKeys = new Set(mutedKeysRef.current).add(key);
    mutedKeysRef.current = nextMutedKeys;
    setMutedKeys(nextMutedKeys);
    queueRef.current = queueRef.current.filter(
      (op) => (op.type === 'add' ? op.entry.key : op.key) !== key
    );
    if (lastKeyRef.current?.key === key) lastKeyRef.current = null;
    setIsolateKey((current) => (current === key ? null : current));
    setEntries((prev) => prev.filter((e) => e.key !== key));
  }, []);

  const unmuteKey = useCallback((key) => {
    const nextMutedKeys = new Set(mutedKeysRef.current);
    nextMutedKeys.delete(key);
    mutedKeysRef.current = nextMutedKeys;
    setMutedKeys(nextMutedKeys);
  }, []);

  const clearMutes = useCallback(() => {
    const nextMutedKeys = new Set();
    mutedKeysRef.current = nextMutedKeys;
    setMutedKeys(nextMutedKeys);
  }, []);

  const toggleIsolate = useCallback((key) => {
    setIsolateKey((prev) => (prev === key ? null : key));
  }, []);

  const togglePin = useCallback((id) => {
    setPinnedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const copyMessage = useCallback(async (entry) => {
    const text = entry.message;
    const done = () => {
      setCopiedId(entry.id);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopiedId(null), 1200);
    };
    let copied = false;
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        copied = true;
      } catch {
        // se intenta el respaldo de abajo
      }
    }
    if (!copied) copied = fallbackCopyText(text);
    if (copied) done();
  }, []);

  const addNote = useCallback(
    (event) => {
      event.preventDefault();
      const location = noteLocation.trim();
      const comment = noteComment.trim();
      if (!location && !comment) return;

      setNotes((prev) => [
        {
          id: Date.now(),
          location,
          comment,
          createdAt: new Date().toLocaleString(),
        },
        ...prev,
      ]);
      setNoteLocation('');
      setNoteComment('');
    },
    [noteLocation, noteComment]
  );

  const removeNote = useCallback((id) => {
    setNotes((prev) => prev.filter((note) => note.id !== id));
  }, []);

  // ── Hábitos ─────────────────────────────────────────────────────
  const markHabitDone = useCallback((id) => {
    setHabits((prev) =>
      prev.map((h) => {
        if (h.id !== id) return h;
        if (h.lockUntil && Date.now() < h.lockUntil) return h;
        return {
          ...h,
          nextDueAt: Date.now() + h.intervalMin * 60000,
          lockUntil: null,
        };
      })
    );
  }, []);

  const snoozeHabit = useCallback((id, minutes = 5) => {
    setHabits((prev) =>
      prev.map((h) => {
        if (h.id !== id) return h;
        if (h.lockUntil && Date.now() < h.lockUntil) return h;
        return {
          ...h,
          nextDueAt: Date.now() + minutes * 60000,
          lockUntil: null,
        };
      })
    );
  }, []);

  const toggleHabitEnabled = useCallback((id) => {
    setHabits((prev) =>
      prev.map((h) => {
        if (h.id !== id) return h;
        if (h.lockUntil && Date.now() < h.lockUntil) return h;
        return {
          ...h,
          enabled: !h.enabled,
          lockUntil: null,
          // Al reactivar, el conteo arranca de nuevo desde ahora
          // en vez de mostrar como vencido algo que estuvo pausado.
          nextDueAt: !h.enabled
            ? Date.now() + h.intervalMin * 60000
            : h.nextDueAt,
        };
      })
    );
  }, []);

  const updateHabitInterval = useCallback((id, minutes) => {
    const clamped = Math.min(240, Math.max(1, Math.round(minutes) || 1));
    setHabits((prev) =>
      prev.map((h) => {
        if (h.id !== id) return h;
        if (h.lockUntil && Date.now() < h.lockUntil) return h;
        return {
          ...h,
          intervalMin: clamped,
          nextDueAt: Date.now() + clamped * 60000,
          lockUntil: null,
        };
      })
    );
  }, []);

  const updateHabitLock = useCallback((id, minutes) => {
    const clamped = Math.min(30, Math.max(1, Math.round(minutes) || 1));
    setHabits((prev) =>
      prev.map((h) => {
        if (h.id !== id) return h;
        if (h.lockUntil && Date.now() < h.lockUntil) return h;
        return { ...h, lockMin: clamped };
      })
    );
  }, []);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const normalizedQuery = useMemo(() => query.toLowerCase(), [query]);
  const visible = useMemo(() => {
    return entries.filter((e) => {
      if (isolateKey) return e.key === isolateKey;
      if (filter !== 'all' && e.level !== filter) return false;
      if (normalizedQuery && !e.message.toLowerCase().includes(normalizedQuery))
        return false;
      return true;
    });
  }, [entries, filter, normalizedQuery, isolateKey]);

  const pinnedEntries = useMemo(
    () => entries.filter((e) => pinnedIds.has(e.id)),
    [entries, pinnedIds]
  );

  const counts = useMemo(
    () =>
      entries.reduce((acc, entry) => {
        acc[entry.level] = (acc[entry.level] ?? 0) + entry.count;
        return acc;
      }, {}),
    [entries]
  );

  const habitsWithStatus = useMemo(
    () =>
      habits.map((h) => ({
        ...h,
        isDue: h.enabled && now >= h.nextDueAt,
        isLocked:
          h.enabled &&
          now >= h.nextDueAt &&
          (!h.lockUntil || h.lockUntil > now),
        minutesLeft: Math.max(0, Math.round((h.nextDueAt - now) / 60000)),
      })),
    [habits, now]
  );

  const dueHabits = useMemo(
    () => habitsWithStatus.filter((h) => h.isDue),
    [habitsWithStatus]
  );
  const dueHabitsCount = dueHabits.length;

  const hasError = (counts.error ?? 0) > 0;
  const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);
  const badgeValue = hasError ? counts.error : totalCount;
  const isolateMessagePreview = isolateKey
    ? isolateKey.replace(LEVEL_KEY_PREFIX, '')
    : null;

  const habitLockOverlay =
    dueHabitsCount > 0 ? (
      <HabitLockOverlay
        habits={dueHabits}
        now={now}
        onDone={markHabitDone}
      />
    ) : null;

  if (!open) {
    return (
      <>
        {habitLockOverlay}
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={
            hasError
              ? `Abrir consola, ${counts.error} error(es)`
              : dueHabitsCount > 0
              ? `Abrir consola, ${dueHabitsCount} hábito(s) pendiente(s)`
              : 'Abrir consola de desarrollo'
          }
          title={
            hasError
              ? `${counts.error} error(es) en consola (Ctrl+\`)`
              : dueHabitsCount > 0
              ? `${dueHabitsCount} hábito(s) pendiente(s) (Ctrl+\`)`
              : 'Abrir consola (Ctrl+`)'
          }
          style={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            zIndex: 999999,
            width: 56,
            height: 56,
            borderRadius: '50%',
            border: hasError ? `2px solid ${ERROR_RED}` : '1px solid #30363d',
            background: hasError
              ? `radial-gradient(circle at 30% 30%, #3a1414, ${MATTE_BLACK})`
              : MATTE_BLACK,
            color: hasError ? ERROR_RED : '#c9d1d9',
            boxShadow: hasError
              ? '0 0 0 4px rgba(248,81,73,0.15), 0 4px 14px rgba(0,0,0,0.5)'
              : '0 4px 14px rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            fontFamily: "'SF Mono', Consolas, Menlo, monospace",
            fontSize: 20,
            animation: hasError
              ? 'dcp-pulse 1.6s ease-in-out infinite'
              : 'none',
            transition: 'transform 0.12s ease',
          }}
          onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.94)')}
          onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
        >
          {hasError ? '⚠' : '›_'}
          {badgeValue > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 18,
                height: 18,
                padding: '0 4px',
                borderRadius: 9,
                background: hasError ? ERROR_RED : '#30363d',
                color: hasError ? '#0d1117' : '#c9d1d9',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              {badgeValue > 99 ? '99+' : badgeValue}
            </span>
          )}
          {dueHabitsCount > 0 && (
            <span
              className="dcp-habit-dot"
              style={{
                position: 'absolute',
                bottom: -2,
                left: -2,
                width: 14,
                height: 14,
                borderRadius: '50%',
                background: HABIT_ACCENT,
                border: `2px solid ${MATTE_BLACK}`,
                animation: 'dcp-habit-pulse 1.8s ease-in-out infinite',
              }}
            />
          )}
        </button>
        <style>{`
          @keyframes dcp-pulse {
            0%, 100% { box-shadow: 0 0 0 4px rgba(248,81,73,0.15), 0 4px 14px rgba(0,0,0,0.5); }
            50% { box-shadow: 0 0 0 8px rgba(248,81,73,0.06), 0 4px 14px rgba(0,0,0,0.5); }
          }
          @keyframes dcp-habit-pulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.3); opacity: 0.7; }
          }
          @media (prefers-reduced-motion: reduce) {
            button[aria-label^="Abrir consola"] { animation: none !important; }
            .dcp-habit-dot { animation: none !important; }
          }
        `}</style>
      </>
    );
  }

  return (
    <>
    {habitLockOverlay}
    <div
      style={{
        position: 'fixed',
        bottom: dueHabitsCount > 0 ? 0 : 20,
        right: dueHabitsCount > 0 ? 0 : 20,
        top: dueHabitsCount > 0 ? 0 : undefined,
        left: dueHabitsCount > 0 ? 0 : undefined,
        zIndex: 999999,
        width: dueHabitsCount > 0 ? '100vw' : 1000,
        maxWidth: dueHabitsCount > 0 ? '100vw' : 'calc(100vw - 32px)',
        maxHeight: dueHabitsCount > 0 ? '100vh' : '65vh',
        height: dueHabitsCount > 0 ? '100vh' : undefined,
        fontFamily: "'SF Mono', Consolas, Menlo, monospace",
        fontSize: 12,
        background: MATTE_BLACK,
        color: '#c9d1d9',
        border: hasError ? `1px solid ${ERROR_RED}` : '1px solid #30363d',
        borderRadius: 16,
        boxShadow: hasError
          ? '0 0 0 3px rgba(248,81,73,0.12), 0 12px 32px rgba(0,0,0,0.55)'
          : '0 12px 32px rgba(0,0,0,0.55)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        aria-label={
          dueHabitsCount > 0
            ? 'Consola bloqueada por un hábito pendiente'
            : 'Minimizar consola de desarrollo'
        }
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 12px',
          borderBottom: '1px solid #21262d',
          cursor: dueHabitsCount > 0 ? 'default' : 'pointer',
          userSelect: 'none',
          background: MATTE_BLACK_SOFT,
          border: 0,
          color: 'inherit',
          font: 'inherit',
          textAlign: 'left',
          width: '100%',
        }}
        onClick={() => {
          if (dueHabitsCount > 0) return;
          setOpen(false);
        }}
      >
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: hasError ? ERROR_RED : '#3fb950',
            flexShrink: 0,
          }}
        />
        <span style={{ fontWeight: 700 }}>Console</span>
        {['log', 'info', 'warn', 'error', 'http'].map((lvl) =>
          counts[lvl] ? (
            <span key={lvl} style={{ color: LEVEL_COLOR[lvl] }}>
              {LEVEL_LABEL[lvl]}:{counts[lvl]}
            </span>
          ) : null
        )}
        {mutedKeys.size > 0 && (
          <span style={{ opacity: 0.6 }}>🚫 {mutedKeys.size}</span>
        )}
        {dueHabitsCount > 0 && (
          <span style={{ color: HABIT_ACCENT }}>
            ● {dueHabitsCount} hábito{dueHabitsCount > 1 ? 's' : ''}
          </span>
        )}
        <span style={{ marginLeft: 'auto', opacity: 0.6 }}>
          {dueHabitsCount > 0 ? 'bloqueo activo' : '▾ minimizar'}
        </span>
      </button>

      <div
        style={{
          display: 'flex',
          gap: 6,
          padding: '6px 10px',
          borderBottom: '1px solid #21262d',
          alignItems: 'center',
          flexWrap: 'wrap',
          opacity: isolateKey ? 0.5 : 1,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {['all', 'log', 'info', 'warn', 'error', 'http'].map((lvl) => (
          <button
            type="button"
            key={lvl}
            onClick={() => setFilter(lvl)}
            disabled={Boolean(isolateKey)}
            aria-pressed={filter === lvl}
            style={{
              background: filter === lvl ? '#21262d' : 'transparent',
              border: '1px solid #30363d',
              color: lvl === 'all' ? '#c9d1d9' : LEVEL_COLOR[lvl],
              borderRadius: 4,
              padding: '2px 8px',
              cursor: isolateKey ? 'not-allowed' : 'pointer',
              fontSize: 11,
            }}
          >
            {lvl}
          </button>
        ))}
        <input
          ref={searchInputRef}
          aria-label="Buscar en los mensajes de consola"
          placeholder="buscar… (/)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={Boolean(isolateKey)}
          style={{
            background: '#161b22',
            border: '1px solid #30363d',
            borderRadius: 4,
            color: '#c9d1d9',
            padding: '3px 8px',
            fontSize: 11,
            flex: 1,
            minWidth: 80,
          }}
        />
        <button
          type="button"
          onClick={toggleCapture}
          aria-pressed={!captureEnabled}
          title={
            captureEnabled
              ? 'Pausar la captura de nuevos logs'
              : 'Activar la captura de logs'
          }
          style={{
            background: captureEnabled ? 'transparent' : 'rgba(248,81,73,0.12)',
            border: `1px solid ${captureEnabled ? '#30363d' : ERROR_RED}`,
            color: captureEnabled ? '#3fb950' : ERROR_RED,
            borderRadius: 4,
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: 11,
          }}
        >
          {captureEnabled ? '🔓 logs activos' : '🔒 logs desactivados'}
        </button>
        <button
          type="button"
          onClick={toggleHttpCapture}
          aria-pressed={!httpCaptureEnabled}
          title={
            httpCaptureEnabled
              ? 'Pausar la captura de solicitudes HTTP'
              : 'Activar la captura de solicitudes HTTP'
          }
          style={{
            background: httpCaptureEnabled
              ? 'transparent'
              : 'rgba(248,81,73,0.12)',
            border: `1px solid ${httpCaptureEnabled ? '#30363d' : ERROR_RED}`,
            color: httpCaptureEnabled ? HTTP_ACCENT : ERROR_RED,
            borderRadius: 4,
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: 11,
          }}
        >
          {httpCaptureEnabled ? '🌐 http activos' : '🌐 http desactivados'}
        </button>
        {mutedKeys.size > 0 && (
          <button
            type="button"
            onClick={clearMutes}
            title="Quitar todos los silencios"
            style={{
              background: 'transparent',
              border: '1px solid #30363d',
              color: '#d29922',
              borderRadius: 4,
              padding: '2px 8px',
              cursor: 'pointer',
              fontSize: 11,
            }}
          >
            reactivar
          </button>
        )}
        <button
          type="button"
          onClick={clear}
          style={{
            background: 'transparent',
            border: '1px solid #30363d',
            color: '#c9d1d9',
            borderRadius: 4,
            padding: '2px 8px',
            cursor: 'pointer',
            fontSize: 11,
          }}
        >
          limpiar
        </button>
      </div>

      {isolateKey && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            background: 'rgba(88,166,255,0.12)',
            borderBottom: `1px solid ${ISOLATE_ACCENT}`,
            fontSize: 11,
          }}
        >
          <span
            style={{ flexShrink: 0, fontWeight: 700, color: ISOLATE_ACCENT }}
          >
            🎯 Aislado (Esc):
          </span>
          <span
            style={{
              flex: 1,
              color: '#c9d1d9',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            title={isolateMessagePreview}
          >
            {isolateMessagePreview}
          </span>
          <button
            type="button"
            onClick={() => setIsolateKey(null)}
            style={{
              flexShrink: 0,
              background: 'transparent',
              border: `1px solid ${ISOLATE_ACCENT}`,
              color: ISOLATE_ACCENT,
              borderRadius: 4,
              padding: '1px 6px',
              cursor: 'pointer',
              fontSize: 11,
            }}
          >
            quitar filtro
          </button>
        </div>
      )}

      <div
        style={{
          borderBottom: '1px solid #21262d',
          background: MATTE_BLACK_SOFT,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={() => setHabitsOpen((current) => !current)}
          aria-expanded={habitsOpen}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            border: 0,
            background: 'transparent',
            color: '#c9d1d9',
            cursor: 'pointer',
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          <span>{habitsOpen ? '▼' : '▶'}</span>
          <strong>Hábitos</strong>
          {dueHabitsCount > 0 ? (
            <span style={{ color: HABIT_ACCENT }}>
              ({dueHabitsCount} pendiente{dueHabitsCount > 1 ? 's' : ''})
            </span>
          ) : (
            <span style={{ opacity: 0.5 }}>(al día)</span>
          )}
        </button>

        {habitsOpen && (
          <div
            style={{
              padding: '0 10px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            {habitsWithStatus.map((h) => (
              <div
                key={h.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 8px',
                  border: `1px solid ${h.isDue ? HABIT_ACCENT : '#21262d'}`,
                  borderRadius: 6,
                  background: h.isDue ? 'rgba(163,113,247,0.08)' : MATTE_BLACK,
                  opacity: h.enabled ? 1 : 0.45,
                }}
              >
                <span style={{ fontSize: 16, flexShrink: 0 }}>{h.emoji}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <div>{h.label}</div>
                  <div style={{ fontSize: 10, opacity: 0.6 }}>
                    {!h.enabled
                      ? 'desactivado'
                      : h.isDue
                      ? '¡es hora!'
                      : `en ${h.minutesLeft} min`}
                  </div>
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                    opacity: 0.8,
                  }}
                >
                  cada
                  <input
                    type="number"
                    min={1}
                    max={240}
                    value={h.intervalMin}
                    disabled={h.isLocked}
                    onChange={(e) =>
                      updateHabitInterval(h.id, Number(e.target.value))
                    }
                    aria-label={`Frecuencia de "${h.label}" en minutos`}
                    style={{
                      width: 42,
                      background: '#161b22',
                      border: '1px solid #30363d',
                      borderRadius: 4,
                      color: '#c9d1d9',
                      padding: '2px 4px',
                      fontSize: 11,
                    }}
                  />
                  min
                </span>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                    flexShrink: 0,
                    opacity: 0.8,
                  }}
                >
                  bloqueo
                  <input
                    type="number"
                    min={1}
                    max={30}
                    value={h.lockMin ?? 1}
                    disabled={h.isLocked}
                    onChange={(e) =>
                      updateHabitLock(h.id, Number(e.target.value))
                    }
                    aria-label={`Bloqueo de "${h.label}" en minutos`}
                    style={{
                      width: 42,
                      background: '#161b22',
                      border: '1px solid #30363d',
                      borderRadius: 4,
                      color: '#c9d1d9',
                      padding: '2px 4px',
                      fontSize: 11,
                    }}
                  />
                  min
                </span>
                {h.isDue && !h.isLocked && (
                  <>
                    <button
                      type="button"
                      onClick={() => markHabitDone(h.id)}
                      style={actionButtonStyle('#3fb950')}
                    >
                      hecho
                    </button>
                    <button
                      type="button"
                      onClick={() => snoozeHabit(h.id, 5)}
                      style={actionButtonStyle('#d29922')}
                    >
                      +5 min
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={h.isLocked}
                  onClick={() => toggleHabitEnabled(h.id)}
                  aria-label={
                    h.enabled ? 'Pausar recordatorio' : 'Activar recordatorio'
                  }
                  title={
                    h.isLocked
                      ? 'No se puede pausar durante el bloqueo'
                      : h.enabled
                      ? 'Pausar recordatorio'
                      : 'Activar recordatorio'
                  }
                  style={iconButtonStyle(
                    h.enabled ? '#8b949e' : '#3fb950',
                    20,
                    h.isLocked
                  )}
                >
                  {h.enabled ? <PauseIcon /> : <PlayIcon />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          borderBottom: '1px solid #21262d',
          background: MATTE_BLACK_SOFT,
          flexShrink: 0,
        }}
      >
        <button
          type="button"
          onClick={() => setNotesOpen((current) => !current)}
          aria-expanded={notesOpen}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '6px 10px',
            border: 0,
            background: 'transparent',
            color: '#c9d1d9',
            cursor: 'pointer',
            font: 'inherit',
            textAlign: 'left',
          }}
        >
          <span>{notesOpen ? '▼' : '▶'}</span>
          <strong>Notas</strong>
          <span style={{ opacity: 0.6 }}>({notes.length})</span>
        </button>

        {notesOpen && (
          <div style={{ padding: '0 10px 8px' }}>
            <form
              onSubmit={addNote}
              style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}
            >
              <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
                <input
                  aria-label="Ruta o ubicación del error"
                  placeholder="ruta o dónde está el error"
                  value={noteLocation}
                  onChange={(event) => setNoteLocation(event.target.value)}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    background: MATTE_BLACK,
                    border: '1px solid #30363d',
                    borderRight: 0,
                    borderRadius: '4px 0 0 4px',
                    color: '#c9d1d9',
                    padding: '5px 7px',
                    font: 'inherit',
                  }}
                />
                <button
                  type="button"
                  onClick={() =>
                    setNoteLocation(
                      `${window.location.pathname}${window.location.search}${window.location.hash}`
                    )
                  }
                  title="Usar la ruta actual"
                  style={{
                    background: '#21262d',
                    border: '1px solid #30363d',
                    borderRadius: '0 4px 4px 0',
                    color: ISOLATE_ACCENT,
                    padding: '2px 7px',
                    cursor: 'pointer',
                    fontSize: 10,
                  }}
                >
                  ruta actual
                </button>
              </div>
              <input
                aria-label="Comentario de la nota"
                placeholder="comentario"
                value={noteComment}
                onChange={(event) => setNoteComment(event.target.value)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  background: MATTE_BLACK,
                  border: '1px solid #30363d',
                  borderRadius: 4,
                  color: '#c9d1d9',
                  padding: '5px 7px',
                  font: 'inherit',
                }}
              />
              <button
                type="submit"
                disabled={!noteLocation.trim() && !noteComment.trim()}
                aria-label="Agregar nota"
                title="Agregar nota"
                style={iconButtonStyle(
                  '#3fb950',
                  26,
                  !noteLocation.trim() && !noteComment.trim()
                )}
              >
                <PlusIcon />
              </button>
            </form>

            {notes.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 4,
                  maxHeight: 150,
                  overflowY: 'auto',
                  marginTop: 7,
                }}
              >
                {notes.map((note) => (
                  <div
                    key={note.id}
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'flex-start',
                      padding: '5px 7px',
                      border: '1px solid #21262d',
                      borderRadius: 5,
                      background: MATTE_BLACK,
                    }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      {note.location && (
                        <strong
                          style={{
                            display: 'block',
                            color: ISOLATE_ACCENT,
                            overflowWrap: 'anywhere',
                          }}
                        >
                          {note.location}
                        </strong>
                      )}
                      {note.comment && (
                        <span
                          style={{ display: 'block', whiteSpace: 'pre-wrap' }}
                        >
                          {note.comment}
                        </span>
                      )}
                      <small style={{ opacity: 0.45 }}>{note.createdAt}</small>
                    </span>
                    <button
                      type="button"
                      onClick={() => removeNote(note.id)}
                      aria-label="Eliminar nota"
                      title="Eliminar nota"
                      style={iconButtonStyle(ERROR_RED, 20)}
                    >
                      <MinusIcon />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sección de fijados: siempre visible, ignora filtro/búsqueda/aislamiento */}
      {pinnedEntries.length > 0 && (
        <div
          style={{
            borderBottom: '1px solid #21262d',
            maxHeight: 140,
            overflowY: 'auto',
            flexShrink: 0,
            background: 'rgba(210,153,34,0.04)',
          }}
        >
          <div
            style={{
              opacity: 0.6,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              padding: '4px 10px 2px',
              color: PIN_ACCENT,
            }}
          >
            📌 Fijados ({pinnedEntries.length})
          </div>
          {pinnedEntries.map((e) => (
            <LogRow
              key={e.id}
              entry={e}
              isolateKey={isolateKey}
              copiedId={copiedId}
              pinnedIds={pinnedIds}
              onCopy={copyMessage}
              onToggleIsolate={toggleIsolate}
              onMute={muteKey}
              onTogglePin={togglePin}
            />
          ))}
        </div>
      )}

      <div
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 0' }}
      >
        {visible.map((e) => (
          <LogRow
            key={e.id}
            entry={e}
            isolateKey={isolateKey}
            copiedId={copiedId}
            pinnedIds={pinnedIds}
            onCopy={copyMessage}
            onToggleIsolate={toggleIsolate}
            onMute={muteKey}
            onTogglePin={togglePin}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {mutedKeys.size > 0 && (
        <div
          style={{
            borderTop: '1px solid #21262d',
            background: MATTE_BLACK_SOFT,
            padding: '6px 8px 6px 10px',
            maxHeight: 130,
            overflowY: 'auto',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              opacity: 0.5,
              marginBottom: 5,
              fontSize: 10,
              textTransform: 'uppercase',
              letterSpacing: 0.5,
            }}
          >
            Silenciados ({mutedKeys.size})
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {[...mutedKeys].map((key) => {
              const [level] = key.split('::');
              const label = key.replace(LEVEL_KEY_PREFIX, '');
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    gap: 8,
                    alignItems: 'center',
                    background: MATTE_BLACK,
                    border: '1px solid #21262d',
                    borderRadius: 6,
                    padding: '5px 6px 5px 8px',
                  }}
                >
                  <span
                    title={LEVEL_LABEL[level] ?? level}
                    style={{
                      flexShrink: 0,
                      width: 7,
                      height: 7,
                      borderRadius: '50%',
                      background: LEVEL_COLOR[level] ?? '#8b949e',
                    }}
                  />
                  <span
                    title={label}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: '#c9d1d9',
                    }}
                  >
                    {label}
                  </span>
                  <button
                    type="button"
                    onClick={() => unmuteKey(key)}
                    style={{
                      flexShrink: 0,
                      width: 62,
                      background: 'transparent',
                      border: '1px solid #30363d',
                      color: '#d29922',
                      borderRadius: 4,
                      padding: '2px 0',
                      cursor: 'pointer',
                      fontSize: 10,
                    }}
                  >
                    reactivar
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <style>{`
        .dcp-actions { opacity: 0; transition: opacity 0.1s ease; }
        .dcp-row:hover .dcp-actions,
        .dcp-row:focus-within .dcp-actions { opacity: 1; }
        @media (hover: none) {
          .dcp-actions { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          .dcp-actions { transition: none; }
        }
      `}</style>
    </div>
    </>
  );
}

/**
 * ── Para mandar los errores a un backend (opcional) ─────────────────
 *   fetch('/api/client-logs', {
 *     method: 'POST',
 *     headers: { 'Content-Type': 'application/json' },
 *     body: JSON.stringify({ level, message, url: location.href }),
 *   }).catch(() => {});
 */
